import { useState, useEffect } from 'react';
import { X, AlertTriangle, ArrowUpCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface MappingNotification {
  notification_id: string;
  mapping_type: 'moodle' | 'tutorlms' | 'learndash';
  mapping_id: string;
  course_name: string;
  old_version_number: number | null;
  created_at: string;
}

interface GroupedNotification {
  study_plan_id: string;
  study_plan_title: string;
  new_version_number: number;
  new_version_approved_at: string;
  new_version_id: string;
  mappings: MappingNotification[];
}

interface VersionNotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

export default function VersionNotificationsPanel({
  isOpen,
  onClose,
  onCountChange
}: VersionNotificationsPanelProps) {
  const [notifications, setNotifications] = useState<GroupedNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch('/api/version-notifications');
      const data = await response.json();

      if (data.success) {
        setNotifications(data.notifications);
        onCountChange?.(data.total_count);
      } else {
        setError(data.error || 'Kunne ikke hente varsler');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Kunne ikke hente varsler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const handleAcknowledge = async (notificationId: string) => {
    setProcessingIds(prev => new Set(prev).add(notificationId));

    try {
      const response = await authenticatedFetch(`/api/version-notifications/${notificationId}/acknowledge`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        // Remove the notification from the list
        setNotifications(prev => {
          const updated = prev.map(group => ({
            ...group,
            mappings: group.mappings.filter(m => m.notification_id !== notificationId)
          })).filter(group => group.mappings.length > 0);

          const newCount = updated.reduce((sum, g) => sum + g.mappings.length, 0);
          onCountChange?.(newCount);

          return updated;
        });
      }
    } catch (err) {
      console.error('Error acknowledging notification:', err);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };

  const handleUpgrade = async (notificationId: string) => {
    setProcessingIds(prev => new Set(prev).add(notificationId));

    try {
      const response = await authenticatedFetch(`/api/version-notifications/${notificationId}/upgrade`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        // Remove the notification from the list
        setNotifications(prev => {
          const updated = prev.map(group => ({
            ...group,
            mappings: group.mappings.filter(m => m.notification_id !== notificationId)
          })).filter(group => group.mappings.length > 0);

          const newCount = updated.reduce((sum, g) => sum + g.mappings.length, 0);
          onCountChange?.(newCount);

          return updated;
        });
      }
    } catch (err) {
      console.error('Error upgrading notification:', err);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };

  const handleUpgradeAll = async (notification: GroupedNotification) => {
    const ids = notification.mappings.map(m => m.notification_id);
    ids.forEach(id => setProcessingIds(prev => new Set(prev).add(id)));

    try {
      const response = await authenticatedFetch('/api/version-notifications/upgrade-all', {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: notification.study_plan_id,
          new_version_id: notification.new_version_id
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove all notifications for this study plan
        setNotifications(prev => {
          const updated = prev.filter(g => g.study_plan_id !== notification.study_plan_id);
          const newCount = updated.reduce((sum, g) => sum + g.mappings.length, 0);
          onCountChange?.(newCount);
          return updated;
        });
      }
    } catch (err) {
      console.error('Error upgrading all:', err);
    } finally {
      ids.forEach(id => {
        setProcessingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
    }
  };

  const handleAcknowledgeAll = async (notification: GroupedNotification) => {
    const ids = notification.mappings.map(m => m.notification_id);
    ids.forEach(id => setProcessingIds(prev => new Set(prev).add(id)));

    try {
      const response = await authenticatedFetch('/api/version-notifications/acknowledge-all', {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: notification.study_plan_id
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove all notifications for this study plan
        setNotifications(prev => {
          const updated = prev.filter(g => g.study_plan_id !== notification.study_plan_id);
          const newCount = updated.reduce((sum, g) => sum + g.mappings.length, 0);
          onCountChange?.(newCount);
          return updated;
        });
      }
    } catch (err) {
      console.error('Error acknowledging all:', err);
    } finally {
      ids.forEach(id => {
        setProcessingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'moodle': return 'Moodle';
      case 'tutorlms': return 'TutorLMS';
      case 'learndash': return 'LearnDash';
      case 'exam_set': return 'Prøvesett';
      case 'course_instance': return 'Kurs';
      default: return type;
    }
  };

  const getTypeBgColor = (type: string) => {
    switch (type) {
      case 'moodle': return 'bg-kf-orange-100 text-kf-orange-800';
      case 'tutorlms': return 'bg-kf-blue-100 text-kf-blue-800';
      case 'learndash': return 'bg-green-100 text-green-800';
      case 'exam_set': return 'bg-purple-100 text-purple-800';
      case 'course_instance': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-25"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-kf-primary text-white">
          <div className="flex items-center space-x-2">
            <AlertTriangle size={20} />
            <h2 className="text-lg font-semibold">Nye versjoner tilgjengelig</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-kf-secondary rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-56px)] p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-gray-400" size={24} />
              <span className="ml-2 text-gray-600">Laster varsler...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
              <button
                onClick={fetchNotifications}
                className="mt-2 text-sm text-kf-primary hover:underline"
              >
                Prøv igjen
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle size={48} className="mx-auto mb-2 text-green-500" />
              <p>Ingen nye versjonsvarsler</p>
              <p className="text-sm mt-1">Alle koblinger og kurs er oppdatert</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Disse studieplanene har fått nye versjoner. Du kan velge å oppgradere til den nye versjonen, eller beholde den gamle.
              </p>

              {notifications.map((notification) => (
                <div
                  key={notification.study_plan_id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Study Plan Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-medium text-gray-900">
                      {notification.study_plan_title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Ny versjon: <span className="font-medium">v{notification.new_version_number}</span>
                      <span className="mx-2">-</span>
                      Godkjent {new Date(notification.new_version_approved_at).toLocaleDateString('nb-NO')}
                    </p>
                  </div>

                  {/* Affected Mappings */}
                  <div className="divide-y">
                    {notification.mappings.map((mapping) => (
                      <div
                        key={mapping.notification_id}
                        className="px-4 py-3 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${getTypeBgColor(mapping.mapping_type)}`}>
                              {getTypeLabel(mapping.mapping_type)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mt-1 truncate">
                            {mapping.course_name || 'Ukjent kurs'}
                          </p>
                          {mapping.old_version_number && (
                            <p className="text-xs text-gray-500">
                              Bruker v{mapping.old_version_number}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleAcknowledge(mapping.notification_id)}
                            disabled={processingIds.has(mapping.notification_id)}
                            className="text-xs px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                            title="Behold gammel versjon"
                          >
                            Behold
                          </button>
                          <button
                            onClick={() => handleUpgrade(mapping.notification_id)}
                            disabled={processingIds.has(mapping.notification_id)}
                            className="text-xs px-2 py-1 bg-kf-primary text-white rounded hover:bg-kf-secondary disabled:opacity-50 flex items-center space-x-1"
                            title="Oppgrader til ny versjon"
                          >
                            {processingIds.has(mapping.notification_id) ? (
                              <RefreshCw size={12} className="animate-spin" />
                            ) : (
                              <ArrowUpCircle size={12} />
                            )}
                            <span>Oppgrader</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Bulk Actions */}
                  {notification.mappings.length > 1 && (
                    <div className="px-4 py-2 bg-gray-50 border-t flex justify-end space-x-2">
                      <button
                        onClick={() => handleAcknowledgeAll(notification)}
                        disabled={notification.mappings.some(m => processingIds.has(m.notification_id))}
                        className="text-xs px-3 py-1.5 text-gray-600 hover:text-gray-900 border rounded disabled:opacity-50"
                      >
                        Behold alle på gammel versjon
                      </button>
                      <button
                        onClick={() => handleUpgradeAll(notification)}
                        disabled={notification.mappings.some(m => processingIds.has(m.notification_id))}
                        className="text-xs px-3 py-1.5 bg-kf-primary text-white rounded hover:bg-kf-secondary disabled:opacity-50 flex items-center space-x-1"
                      >
                        <ArrowUpCircle size={12} />
                        <span>Oppgrader alle til v{notification.new_version_number}</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
